import React, { useCallback, useMemo } from "react";

import Card from "./Card";
import Calendar from "./Calendar";

import { useAppStore } from "@store/appStateStore";

import "./DashboardCard.css";
import AddAttendanceForParticipantsForm from "@components/form/attendance/AddAttendanceForParticipantsForm";
import AttendancesTable from "@components/stat/attendance/table/AttendancesTable";

const CoachDashboardCard: React.FC = () => {
  const { attendances, directions } = useAppStore();

  // Получение уникальных дат посещений для календаря с направлением
  const getAttendanceDates = useCallback((): Array<{
    date: string;
    direction: string | number;
  }> => {
    // Группируем по дате, сохраняем direction_id первого посещения
    const dateMap = new Map<string, string | number>();
    attendances.forEach((attendance) => {
      // attendance.date может быть в формате "2024-12-31" или "2024-12-31T00:00:00.000Z"
      const date = new Date(attendance.date);
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, "0");
      const day = String(date.getDate()).padStart(2, "0");
      const dateStr = `${year}-${month}-${day}`;
      // Если дата ещё не добавлена, добавляем с direction_id
      if (!dateMap.has(dateStr)) {
        dateMap.set(dateStr, String(attendance.direction_id || 0));
      } else {
        const dir = String(dateMap.get(dateStr));
        dateMap.set(dateStr, dir + String(attendance.direction_id || 0));
      }
    });

    // Функция для нахождения моды (самой часто встречающейся цифры)
    // При равной частоте возвращает наименьшую цифру
    function modeOfDigits(str: string): number | null {
      if (str.length === 0) return null;

      const freq: Record<string, number> = {};
      for (const ch of str) {
        freq[ch] = (freq[ch] || 0) + 1;
      }

      let maxFreq = -1;
      let resultDigit: string | null = null;

      for (const [digit, count] of Object.entries(freq)) {
        if (
          count > maxFreq ||
          (count === maxFreq && digit < (resultDigit ?? "9"))
        ) {
          maxFreq = count;
          resultDigit = digit;
        }
      }

      return resultDigit !== null ? Number(resultDigit) : null;
    }

    dateMap.forEach((v, k) => {
      dateMap.set(k, modeOfDigits(String(v)) || 0);
    });

    return Array.from(dateMap.entries()).map(([date, direction]) => ({
      date,
      direction,
    }));
  }, [attendances]);

  // Функция для получения контента тултипа по дате
  const getTooltipContent = useMemo(
    () =>
      (date: string): React.ReactNode => {
        // Приводим дату к формату YYYY-MM-DD для сравнения
        const targetDate = new Date(date);
        const targetDateStr = targetDate.toISOString().split("T")[0]; // "YYYY-MM-DD"

        // Фильтруем посещения участника за эту дату
        const dayAttendances = attendances.filter((a) => {
          const attendanceDate = new Date(a.date);
          const attendanceDateStr = attendanceDate.toISOString().split("T")[0];
          return attendanceDateStr === targetDateStr;
        });

        if (dayAttendances.length === 0) {
          return <div>Нет посещений</div>;
        }

        // Группируем по direction_id для суммирования amount
        const grouped: Record<
          number,
          {
            count: number;
            directionName: string | null;
            directionId: number | null;
          }
        > = {};
        dayAttendances.forEach((att) => {
          const dir = directions.find((d) => d.id === att.direction_id);
          const dirName = dir ? dir.name : null;
          const dirId = dir ? dir.id : null;
          if (!grouped[att.direction_id]) {
            grouped[att.direction_id] = {
              count: 0,
              directionName: dirName || "ptshk",
              directionId: dirId,
            };
          }
          grouped[att.direction_id].count += 1;
        });

        return (
          <div className="attendance-tooltip">
            <div style={{ fontWeight: "bold", marginBottom: "4px" }}>
              Посещения за {targetDate.toLocaleDateString("ru-RU")}
            </div>
            {Object.entries(grouped).map(([dirId, data]) => {
              return (
                <div key={dirId} style={{ marginBottom: "2px" }}>
                  {data.directionName && <span>{data.directionName} </span>}
                  <span>{data.count} участников</span>
                </div>
              );
            })}
            <div style={{ marginTop: "6px", fontSize: "12px", color: "#666" }}>
              Всего участников: {dayAttendances.length}
            </div>
          </div>
        );
      },
    [attendances, directions],
  );

  const attendanceDates = useMemo(
    () => getAttendanceDates(),
    [getAttendanceDates],
  );

  return (
    <>
      <Card title="Добавить занятие">
        <AddAttendanceForParticipantsForm />
      </Card>
      <Card>
        <Calendar
          selectedDates={attendanceDates}
          onDateSelect={() => {}}
          onDateRemove={() => {}}
          dateSectionSow={false}
          getTooltipContent={getTooltipContent}
        ></Calendar>
        <AttendancesTable />
      </Card>
    </>
  );
};

export default CoachDashboardCard;
